import router from '@/router'
import { AppService } from '@/services/app.service'
import { RoleService } from '@/services/role.service'
import { CampaignService } from '@/services/campaign.service'
import { BSAppService } from '@/services/bsapp.service'
import { UserService } from '@/services/user.service'
import { SearchService } from '@/services/search.service'
import { ShopService } from '@/services/shop.service'
import { PointSettingsService } from '@/services/pointSettings.service'
import { WholesalerService } from '@/services/wholesaler.service'
import { VoucherService } from '@/services/voucher.service'
import { MainBoxService } from '@/services/mainbox.service'
import { HTTP_STATUS_CODES } from '@/configs/constants.ts'
import { commitPaginationFromMeta, showErrorToast } from './helpers'
import { normalizeContextPermission } from '@/helpers/contextPermission'

export const systemActions = {
  async searchCheck({ commit }, data) {
    const response = await SearchService.searchCheck(data)
    commit('SET_CHECKS', response.data.data)
  },

  async loadRoles({ commit }) {
    const response = await RoleService.roles()
    commit('SET_ROLES', response.data.data)
  },

  async loadRolesSelect({ commit }) {
    const response = await RoleService.rolesSelect()
    commit('SET_ROLES_SELECT', response.data.data)
  },

  async loadRoleById({ commit }, id) {
    const response = await RoleService.role(id)
    commit('SET_ROLE', response.data.data)
  },

  async saveRole({ commit, state }, data) {
    try {
      const response = await UserService.saveRole(data?.id, {
        data: {
          name: data.name
        },
        permissions: state.permissionsModel
      })
      if (response) {
        router.push({ name: 'roles_list' })
      }
    } catch (err) {
      return err
    }
  },

  async loadBSApps({ commit }) {
    const response = await BSAppService.apps()
    commit('SET_APPS', response.data)
  },

  async loadAppById({ commit }, id) {
    const response = await BSAppService.app(id)
    const data = response.data
    data.context_permission = normalizeContextPermission(data.context_permission)
    commit('SET_APP', data)
  },

  async loadCampaigns({ commit }) {
    const response = await CampaignService.list()
    commit('SET_CAMPAIGNS', response.data)
  },

  async loadCampaign({ commit }, id) {
    const response = await CampaignService.show(id)
    commit('SET_CAMPAIGN', response.data)
  },

  async removeCampaign({ commit }, id) {
    try {
      return await CampaignService.remove(id)
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async removeApp({ commit }, id) {
    try {
      return await AppService.removeApp(id)
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async changeStateApp({ commit }, id) {
    try {
      return await AppService.changeStateApp(id)
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadPermissions({ commit }) {
    try {
      const response = await UserService.permissions()
      commit('SET_PERMISSIONS', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadPermissionsForSelect({ commit }) {
    const response = await UserService.permissionsForSelect()
    commit('SET_PERMISSIONS_SELECT', response.data.data)
  },

  async loadIpcFiles({ commit }) {
    const response = await UserService.ipcFiles()
    commit('SET_FILES', response.data)
  },

  async loadUserFiles({ commit }) {
    const response = await UserService.userFiles()
    commit('SET_USER_FILES', response.data)
  },

  async startIpc({ commit }, filename) {
    const response = await UserService.startIpcImport(filename)
    commit('SET_IMPORT', response.data?.success)
  },

  async startUser({ commit }, form) {
    const response = await UserService.startUserImport(form)
    commit('SET_IMPORT', response.data?.success)
  },

  async loadImportSchema({ commit }) {
    const response = await UserService.importSchema()
    commit('SET_IMPORT_SCHEMA', response?.data?.data ?? response?.data ?? null)
  },

  async loadSetting({ commit }, slug) {
    try {
      const response = await UserService.getSettings(slug)
      if (response.status == HTTP_STATUS_CODES.OK) {
        commit('SET_SETTING', response.data)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async setSetting({ commit }, data) {
    try {
      await UserService.setSetting(data)
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async saveFeaturedProducts({ commit }, data) {
    try {
      await UserService.saveFeaturedProducts(data)
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadFeaturedProducts({ commit }) {
    try {
      const response = await UserService.loadFeaturedProducts()
      if (response.status == HTTP_STATUS_CODES.OK) {
        commit('SET_FEATURED_PRODUCTS', response.data)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  addToast({ commit }, data) {
    commit('SET_TOAST', data)
  },

  addPending({ commit }, isStarting) {
    commit('ADJUST_PENDING', isStarting ? 1 : -1)
  },

  setMenuVisible({ commit }, data) {
    commit('SET_MENU_VISIBLE', data)
  },

  async loadStats({ commit }) {
    try {
      const response = await AppService.getStats()
      if (response.status == HTTP_STATUS_CODES.OK) {
        commit('SET_STATS', response.data)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadWholesalerManagement({ commit }) {
    try {
      const response = await WholesalerService.wholesalerManagements()
      if (response.status == HTTP_STATUS_CODES.OK) {
        commit('SET_WHOLESALER_MANAGEMENT', response.data.data)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadPointSettings({ commit }) {
    try {
      const response = await PointSettingsService.loadPointSettings()
      commit('SET_POINT_SETTINGS', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadShopLimits({ commit }) {
    try {
      const response = await ShopService.loadShopLimitList()
      commit('SET_SHOP_LIMITS', response.limit)
      commit('SET_BONUS_GOALS', response.bonusGoals)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadVoucherSelect({ commit }) {
    try {
      const response = await VoucherService.select()

      if (response.status == HTTP_STATUS_CODES.OK) {
        commit('SET_VOUCHER_SELECT', response.data.data)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadVouchers({ commit }, data) {
    try {
      const response = await VoucherService.vouchers(data)

      if (response.status == HTTP_STATUS_CODES.OK) {
        commit('SET_VOUCHERS', response.data.data)
        commitPaginationFromMeta(commit, response)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadMainBoxes({ commit }, data) {
    const response = await MainBoxService.getMainBoxes(data)
    commit('SET_MAIN_BOXES', response.data)
  },

  async loadMainBox({ commit }, id) {
    const response = await MainBoxService.getMainBox(id)
    commit('SET_MAIN_BOX', response.data)
  }
}
